import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { callApi } from "../services/api.service";

const apiServerUrl = import.meta.env.VITE_API_SERVER_URL;

export interface SuggestedSkillsResponse {
  technical: string[];
  soft: string[];
  source: "cached" | "llm" | "fallback";
}

export const suggestedSkillsKeys = {
  all: ["suggestedSkills"] as const,
  detail: (jobId: string) => [...suggestedSkillsKeys.all, jobId] as const,
};

/**
 * Fetches AI-suggested skills for a job.
 * Results are cached for 5 minutes to avoid redundant LLM calls.
 */
export const useSuggestedSkills = (jobId: string) => {
  const { getAccessTokenSilently } = useAuth0();

  return useQuery<SuggestedSkillsResponse, Error>({
    queryKey: suggestedSkillsKeys.detail(jobId),
    queryFn: async () => {
      const accessToken = await getAccessTokenSilently();
      const response = await callApi<SuggestedSkillsResponse>(
        `${apiServerUrl}/api/jobs/offers/${jobId}/exam-config/suggested-skills`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );
      if (response.error) {
        throw new Error(response.error);
      }
      return response.data as SuggestedSkillsResponse;
    },
    enabled: !!jobId,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });
};
